// Firebase設定
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  connectFirestoreEmulator,
  serverTimestamp,
} from 'firebase/firestore';
import {
  getAuth,
  connectAuthEmulator,
  signInAnonymously,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  linkWithCredential,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
} from 'firebase/auth';

// Firebase設定（本番用は環境変数から取得）
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'five-league.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'five-league',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'five-league.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:123456789:web:abcdef',
};

// アプリ初期化
const app = initializeApp(firebaseConfig);

// Firestore
export const db = getFirestore(app);

// Auth
export const auth = getAuth(app);

// 認証状態をローカルストレージに永続化
setPersistence(auth, browserLocalPersistence).catch(console.error);

// エミュレータ接続
const useEmulator = import.meta.env.VITE_USE_EMULATORS === '1' ||
                   import.meta.env.VITE_USE_EMULATORS === 'true' ||
                   window.location.hostname === 'localhost';

if (useEmulator) {
  const host = window.location.hostname || 'localhost';
  try {
    connectFirestoreEmulator(db, host, 8082);
    connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
    console.log('[Firebase] Emulator connected:', host);
  } catch (e) {
    // 既に接続済みの場合はスキップ
    if (!e.message?.includes('already')) {
      console.warn('[Firebase] Emulator connection failed:', e);
    }
  }
}

// 匿名認証（メール認証済みユーザーは上書きしない）
let authPromise = null;
export async function ensureAuth() {
  if (authPromise) return authPromise;

  authPromise = new Promise((resolve) => {
    // 認証状態の復元を待つ
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe(); // 1回だけ実行

      // 既にログイン済み（メール認証含む）ならそのまま返す
      if (user) {
        resolve(user);
        return;
      }

      // 未ログインの場合のみ匿名認証
      try {
        const result = await signInAnonymously(auth);
        resolve(result.user);
      } catch (e) {
        console.error('[Firebase] Auth error:', e);
        resolve(null);
      }
    });
  });

  return authPromise;
}

// サーバータイムスタンプ
export const ts = serverTimestamp;

// 管理者メールアドレス
export const ADMIN_EMAIL = 'info@quiz-oneword.com';

/**
 * 匿名ユーザーをメール/パスワード認証にアップグレード
 */
export async function upgradeToEmailAuth(email, password) {
  if (!auth.currentUser) {
    throw new Error('ログインしていません');
  }
  const credential = EmailAuthProvider.credential(email, password);
  return await linkWithCredential(auth.currentUser, credential);
}

/**
 * 新規ユーザー登録（メール/パスワード）
 */
export async function registerWithEmail(email, password) {
  return await createUserWithEmailAndPassword(auth, email, password);
}

/**
 * メール/パスワードでログイン
 */
export async function loginWithEmail(email, password) {
  return await signInWithEmailAndPassword(auth, email, password);
}

/**
 * ログアウト
 */
export async function logout() {
  authPromise = null; // 認証プロミスをリセット
  return await firebaseSignOut(auth);
}

/**
 * 認証状態の監視
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

/**
 * 現在のユーザーがメール認証済みかどうか
 */
export function isEmailUser() {
  return auth.currentUser?.email != null;
}

/**
 * 現在のユーザーが管理者かどうか
 */
export function isAdmin() {
  return auth.currentUser?.email === ADMIN_EMAIL;
}

export default app;
